import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Filter } from '../jsonapi/filter';
import { FilterFieldConfig } from './filter-field.model';

let nextInstanceId = 0;

@Component({
    selector: 'app-filter-panel',
    templateUrl: './filter-panel.component.html',
    styleUrls: ['./filter-panel.component.scss'],
    standalone: false
})
export class FilterPanelComponent implements OnInit {
  @Input() fields: FilterFieldConfig[] = [];
  @Input() quickSearchFilter?: Filter;
  @Input() quickSearchPlaceholder = 'Buscar…';

  @Output() search = new EventEmitter<void>();

  @ViewChild('editor') editor!: OverlayPanel;
  @ViewChild('moreMenu') moreMenu!: OverlayPanel;

  editing: FilterFieldConfig | null = null;
  menuSearch = '';

  private extraLabels: string[] = [];
  private appliedSignature = '';
  private editorVisible = false;
  private pending: { field: FilterFieldConfig; target: HTMLElement } | null = null;
  private snapshot: { from: any; to: any } | null = null;
  private readonly instanceId = nextInstanceId++;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.markApplied();
  }

  get shownFields(): FilterFieldConfig[] {
    const base = this.fields.filter(f => f.base);
    const extra = this.fields.filter(f => !f.base && (this.extraLabels.includes(f.label) || this.isSet(f)));
    extra.sort((a, b) => this.extraOrder(a) - this.extraOrder(b));
    return [...base, ...extra];
  }

  get menuFields(): FilterFieldConfig[] {
    const term = this.menuSearch.trim().toLowerCase();
    return this.fields.filter(f => !f.base && (!term || f.label.toLowerCase().includes(term)));
  }

  get extraCount(): number {
    return this.shownFields.filter(f => !f.base).length;
  }

  isShown(field: FilterFieldConfig): boolean {
    return this.shownFields.includes(field);
  }

  isSet(field: FilterFieldConfig): boolean {
    if (field.type === 'bool') return field.filter.value === true;
    return this.hasValue(field.filter.value) || (!!field.filterTo && this.hasValue(field.filterTo.value));
  }

  onChipClick(field: FilterFieldConfig, target: HTMLElement): void {
    if (field.type !== 'bool') {
      this.openEditor(field, target);
      return;
    }
    field.filter.value = field.filter.value === true ? null : true;
    this.applyIfChanged();
  }

  chipValue(field: FilterFieldConfig): string {
    const from = field.filter.value;
    const to = field.filterTo?.value;

    if (field.type === 'multiselect' && Array.isArray(from)) {
      const labels = from.map((v: any) => this.optionLabel(field, v));
      return labels.length > 2 ? `${labels.slice(0, 2).join(', ')} +${labels.length - 2}` : labels.join(', ');
    }
    if (field.type === 'select') return this.optionLabel(field, from);
    if (field.type === 'date') return this.rangeText(this.dateText(from), this.dateText(to), 'desde', 'hasta');
    if (field.type === 'number') {
      const suffix = (field.suffix ?? '').trim();
      const text = this.rangeText(this.numberText(from), this.numberText(to), '≥', '≤');
      return suffix ? `${text} ${suffix}` : text;
    }
    return String(from);
  }

  openEditor(field: FilterFieldConfig, target: HTMLElement): void {
    if (this.editorVisible) {
      this.pending = { field, target };
      this.editor.hide();
      return;
    }
    this.startEdit(field, target);
  }

  onEditorShow(): void {
    this.editorVisible = true;
  }

  onEditorHide(): void {
    this.editorVisible = false;
    this.editing = null;
    this.snapshot = null;
    this.applyIfChanged();

    if (this.pending) {
      const { field, target } = this.pending;
      this.pending = null;
      this.startEdit(field, target);
    }
  }

  confirmEdit(): void {
    this.editor.hide();
  }

  cancelEdit(): void {
    if (this.editing && this.snapshot) {
      this.editing.filter.value = this.snapshot.from;
      if (this.editing.filterTo) this.editing.filterTo.value = this.snapshot.to;
    }
  }

  onSelectChange(): void {
    if (this.editing?.type === 'select') this.editor.hide();
  }

  clearField(field: FilterFieldConfig): void {
    field.filter.value = null;
    if (field.filterTo) field.filterTo.value = null;
    this.extraLabels = this.extraLabels.filter(l => l !== field.label);
    this.applyIfChanged();
  }

  toggleExtra(field: FilterFieldConfig, checked: boolean): void {
    if (!checked) {
      this.clearField(field);
      return;
    }

    this.extraLabels = [...this.extraLabels, field.label];
    this.moreMenu.hide();

    if (field.type === 'bool') {
      field.filter.value = true;
      this.applyIfChanged();
      return;
    }

    this.cd.detectChanges();

    const chip = document.getElementById(this.chipId(field));
    if (chip) setTimeout(() => this.openEditor(field, chip));
  }

  chipId(field: FilterFieldConfig): string {
    return `fp-chip-${this.instanceId}-${this.fields.indexOf(field)}`;
  }

  onQuickSearch(): void {
    this.applyIfChanged();
  }

  clearQuickSearch(): void {
    if (!this.quickSearchFilter) return;
    this.quickSearchFilter.value = null;
    this.applyIfChanged();
  }

  private startEdit(field: FilterFieldConfig, target: HTMLElement): void {
    this.editing = field;
    this.snapshot = { from: field.filter.value, to: field.filterTo?.value ?? null };
    this.cd.detectChanges();
    this.editor.show(null, target);
  }

  private applyIfChanged(): void {
    if (this.signature() === this.appliedSignature) return;
    this.markApplied();
    this.search.emit();
  }

  private extraOrder(field: FilterFieldConfig): number {
    const index = this.extraLabels.indexOf(field.label);
    return index < 0 ? -1 : index;
  }

  private optionLabel(field: FilterFieldConfig, value: any): string {
    return field.options?.find(o => o.value === value)?.label ?? String(value);
  }

  private dateText(value: any): string {
    return value instanceof Date ? value.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  }

  private numberText(value: any): string {
    return this.hasValue(value) ? Number(value).toLocaleString('es-ES') : '';
  }

  private rangeText(from: string, to: string, fromPrefix: string, toPrefix: string): string {
    if (from && to) return `${from} – ${to}`;
    return from ? `${fromPrefix} ${from}` : `${toPrefix} ${to}`;
  }

  private hasValue(v: any): boolean {
    if (Array.isArray(v)) return v.length > 0;
    return v !== null && v !== undefined && v !== '';
  }

  private markApplied(): void {
    this.appliedSignature = this.signature();
  }

  private signature(): string {
    const norm = (v: any): any => {
      if (v === '' || v === undefined) return null;
      if (v instanceof Date) return v.getTime();
      if (Array.isArray(v)) return v.length ? [...v].sort() : null;
      return v;
    };
    return JSON.stringify({
      qs: this.quickSearchFilter ? norm(this.quickSearchFilter.value) : null,
      vals: this.fields.map(f => [norm(f.filter.value), norm(f.filterTo?.value)])
    });
  }
}
